import { useEffect, useRef, useState } from 'react';

import { cn } from '@poseidon/components/cn';

import type { SequenceStep, SequenceStepType } from '~/components/Sequence/sequenceSteps';
import type { LineAddr, StepAddr } from '~/components/Sequence/sequenceMove';
import type { WaitUnit } from '~/components/Sequence/WaitDurationInput';
import { SequenceBranchLine } from '~/components/Sequence/SequenceBranchLine';
import { SequenceGraphInsert } from '~/components/Sequence/SequenceGraphInsert';
import { SequenceParallelChip } from '~/components/Sequence/SequenceParallelChip';
import {
  insertDisabledReason,
  newSequenceStep,
  sequenceStepIssue,
  stepIsEditable,
  stepOpensEditorOnInsert,
} from '~/components/Sequence/sequenceSteps';

interface Props {
  /** The container step being edited — a parallel `group` (fan-out → AND-join) or a `spawn` (1 branch). */
  step: SequenceStep;
  /**
   * Persist the updated container step (the editor autosaves it). May also receive a NON-container step:
   * when a `group` collapses to one arm it hands back that lone step, so the editor unwraps the node.
   */
  onChange: (next: SequenceStep) => void;
  /**
   * Remove this whole container node from the sequence. Called when deleting the last step empties the
   * container (a group / spawn with 0 steps is meaningless) — see {@link removeAt}. Wired by the editor.
   */
  onRemove?: () => void;
  /** Begin dragging a group arm (chip) out onto the spine. Wired by the editor for `group` nodes only. */
  onDragOutArm?: (armIndex: number, e: React.PointerEvent) => void;
  /** The arm index currently being dragged out (dim its chip), or null. */
  draggingArm?: number | null;
  /** Open the content editor for the step at `armIndex`. Wired by the editor for `group` and `spawn` nodes. */
  onEditArm?: (armIndex: number) => void;
  /** A `spawn`'s arm line address — tags its steps for cross-surface drag (passed straight to the
   *  underlying SequenceBranchLine). Omit → the spawn's steps aren't drag-tagged. Unused for a `group`. */
  spawnLineAddr?: LineAddr;
  /** Start dragging a spawn-arm step (a cross-surface move). Only meaningful with `spawnLineAddr`. */
  onStepDrag?: (addr: StepAddr, e: React.PointerEvent) => void;
  /**
   * Fires when the inline add-step picker opens / closes. The editor uses it to raise this node's
   * wrapper above its siblings while the picker is open — otherwise the picker, which renders inside the
   * wrapper's `z-10` stacking context, is painted over by the (z-20) insert slots and faces.
   */
  onPickerOpenChange?: (open: boolean) => void;
  /**
   * Render as the spine node itself: a bordered button group of the steps, with NO caption and an
   * opaque background (to occlude the spine connector behind it). Used for a `group` — its parallel
   * steps ARE the node. Off (the default) renders the captioned "runs alongside" box drawn beside the
   * node (a `spawn`) as a full vertical sub-line.
   */
  inline?: boolean;
  /**
   * Open the add-step picker once on mount. Set by the editor for a container just inserted from the
   * "+" menu, so it lands ready to add its first step (like a drag-merge lands with steps already in)
   * instead of as an empty box.
   */
  autoOpenPicker?: boolean;
  /**
   * Caption above a `spawn`'s branch line. Defaults to "Runs alongside · main line continues" (the
   * spine). A spawn shown inside a branch passes `null` — the spawn NODE above it already labels it,
   * so the caption would be redundant.
   */
  spawnCaption?: string | null;
  /**
   * Locked view (the per-contact contact-detail graph): freeze structure — no add "+", no chip removal,
   * no drag-out, static wait durations. Editing an arm's content (body/subject) via `onEditArm` still works.
   */
  readOnly?: boolean;
}

/** The flat step list a container box shows: a group's single-step arms flattened, or a spawn's branch. */
function stepsOf(step: SequenceStep): SequenceStep[] {
  if (step.type === 'group') return (step.arms ?? []).map((a) => a[0]).filter((s): s is SequenceStep => s != null);
  return step.arm ?? [];
}

/** Write a flat step list back onto the container: one single-step arm per step (group), or the branch (spawn). */
function withSteps(step: SequenceStep, steps: SequenceStep[]): SequenceStep {
  if (step.type === 'group') return { ...step, type: 'group', arms: steps.map((s) => [s]), join: 'all' };
  return { ...step, arm: steps };
}

/**
 * Editor for a parallel container.
 *
 * A `group` (inline) is drawn as a single box of step "chips" — a button group whose chips all run at
 * once (waits-for-all); each chip is its own single-step arm.
 *
 * A `spawn` (the captioned "Runs alongside" line) delegates to {@link SequenceBranchLine} — the shared
 * vertical sub-line (steps stacked with an insert slot at every gap, each a real node) that a switch's
 * route branches also use. Its branch runs alongside the main line, authored like the main spine.
 */
export function SequenceParallelArms({
  step,
  onChange,
  onRemove,
  onDragOutArm,
  draggingArm,
  onEditArm,
  spawnLineAddr,
  onStepDrag,
  onPickerOpenChange,
  inline,
  autoOpenPicker,
  spawnCaption = 'Runs alongside · main line continues',
  readOnly = false,
}: Props) {
  const isGroup = step.type === 'group';
  const steps = stepsOf(step);
  // Group: a single trailing "+" ("adding"). (A spawn's per-gap inserts are owned by SequenceBranchLine.)
  const [adding, setAdding] = useState(false);
  // Keep the setter + the "raise my wrapper" signal in lockstep.
  const setAddingOpen = (open: boolean) => {
    setAdding(open);
    onPickerOpenChange?.(open);
  };

  // A group just inserted from the "+" menu opens its add-step picker immediately (see
  // SequenceGraphEditor), so you land ready to add the first chip instead of on an empty box. One-shot.
  // (A spawn's auto-open is owned by SequenceBranchLine via `autoOpenFirstPicker`.)
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenPicker !== true || autoOpenedRef.current || !isGroup) return;
    autoOpenedRef.current = true;
    setAdding(true);
    onPickerOpenChange?.(true);
  }, [autoOpenPicker, isGroup, onPickerOpenChange]);

  useEffect(() => {
    if (!adding) return;
    const close = () => {
      setAdding(false);
      onPickerOpenChange?.(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest('[data-sequence-picker], [data-arm-insert]') == null) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [adding, onPickerOpenChange]);

  const writeSteps = (next: SequenceStep[]) => onChange(withSteps(step, next));
  // Group: append a chip. Spawn: insert at `slot` (0..length) — a full-line insert.
  const addStep = (type: SequenceStepType) => {
    setAddingOpen(false);
    const newIndex = steps.length;
    writeSteps([...steps, newSequenceStep(type)]);
    if (onEditArm != null && stepOpensEditorOnInsert(type)) onEditArm(newIndex);
  };
  const insertAt = (slot: number, type: SequenceStepType) => {
    const next = steps.slice();
    next.splice(slot, 0, newSequenceStep(type));
    writeSteps(next);
    // Steps you almost always write into open their editor immediately (see stepOpensEditorOnInsert).
    if (onEditArm != null && stepOpensEditorOnInsert(type)) onEditArm(slot);
  };
  const removeAt = (i: number) => {
    const next = steps.filter((_, j) => j !== i);
    // A container is only meaningful with steps in it, so removing the last one removes the whole node
    // (an empty group / spawn box shouldn't linger). A parallel group additionally needs ≥2 steps — a
    // group of one is just a step — so dropping to exactly one collapses back to that lone step, which
    // the editor unwraps onto the spine.
    if (next.length === 0 && onRemove != null) onRemove();
    else if (isGroup && next.length === 1 && next[0] != null) onChange(next[0]);
    else writeSteps(next);
  };
  const setWaitAt = (i: number, waitDays: number, waitUnit: WaitUnit) =>
    writeSteps(steps.map((s, j) => (j === i ? { ...s, waitDays, waitUnit } : s)));
  const setDeadlineAt = (i: number, deadlineDays: number) =>
    writeSteps(steps.map((s, j) => (j === i ? { ...s, deadlineDays } : s)));

  // ── Spawn: a full vertical sub-line (the shared branch line — steps stacked with an insert at every gap). ──
  if (!isGroup) {
    return (
      <SequenceBranchLine
        steps={steps}
        lineAddr={spawnLineAddr}
        onStepDragStart={
          onStepDrag != null && spawnLineAddr != null ? (slot, e) => onStepDrag({ line: spawnLineAddr, index: slot }, e) : undefined
        }
        caption={spawnCaption ?? undefined}
        onInsert={readOnly ? undefined : insertAt}
        onRemoveAt={readOnly ? undefined : removeAt}
        onEditAt={onEditArm}
        onChangeWaitAt={readOnly ? undefined : setWaitAt}
        onChangeDeadlineAt={readOnly ? undefined : setDeadlineAt}
        autoOpenFirstPicker={autoOpenPicker}
        onPickerOpenChange={onPickerOpenChange}
        readOnly={readOnly}
      />
    );
  }

  // ── Group: a compact horizontal button-group of single-step chips (all run at once). Rendered inline as
  //    the spine node (no caption) or, when drawn beside the spine, with the "runs in parallel" caption. ──
  return (
    <div
      data-arm-insert
      className={cn(
        'inline-flex flex-col gap-1.5 rounded-xl border border-violet-300 shadow-sm dark:border-violet-500/40',
        inline ? 'bg-white p-1.5 dark:bg-slate-900' : 'bg-white/70 p-2.5 dark:bg-slate-900/60',
      )}>
      {!inline && (
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Runs in parallel · waits for all
        </span>
      )}
      <div className="flex items-center gap-1.5">
        {steps.length === 0 && <span className="pr-1 text-[11px] italic text-red-400">add a step</span>}
        {steps.map((s, i) => (
          <SequenceParallelChip
            key={`chip-${i}`}
            step={s}
            onRemove={readOnly ? undefined : () => removeAt(i)}
            onChangeWait={readOnly ? undefined : (days, unit) => setWaitAt(i, days, unit)}
            onDragStart={!readOnly && onDragOutArm != null ? (e) => onDragOutArm(i, e) : undefined}
            dimmed={draggingArm === i}
            onEdit={onEditArm != null && stepIsEditable(s) ? () => onEditArm(i) : undefined}
            // Context-aware like the spine and spawn/switch branches — flags a subject-less email chip
            // (validated against the group's own arms, so a grouped email is judged in isolation). A plain
            // per-step check (stepContentIssue) skips email subjects, leaving grouped emails un-ringed.
            invalid={sequenceStepIssue(steps, i)}
            readOnly={readOnly}
          />
        ))}
        {!readOnly && (
          <div data-arm-insert>
            <SequenceGraphInsert
              active={adding}
              onOpen={() => setAddingOpen(true)}
              onPick={addStep}
              onCancel={() => setAddingOpen(false)}
              disabledReason={(type) => insertDisabledReason([], 0, type)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
